import { useState } from '@hookstate/core';

import { Icon as BlueIcon } from '@blueprintjs/core';

import AvatarDefaultImg from '@assets/svg/avatar-placeholder.svg';
import ProfileDefaultPNG from '@assets/png/profile-default.png';

import { useLocalStorage, useToast } from '@hooks';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { getUserMembers } from '@services/queries/studios';

import { patchTask } from '@services/queries/tasks';
import { ITaskRawQuery } from '@models/Task.model';

import { nameToShortName } from '@utils/nameToShortName/nameToShortName';
import { TaskAssigneeLabelLoading } from './TaskAssigneeLabel.loading';
import {
  Container,
  Close,
  Header,
  Title,
  Label,
  Divider,
  Item,
  Icon,
  InputContainer,
  InputSearch,
  SearchIconContainer,
  Items,
  SearchCloseIconContainer,
  ItemNoFeedback,
  Remove,
  UsersSeparator,
  Image,
  LabelContainer,
  LabelImage,
  Name,
  ImageEmpty,
  EmptyName,
  Dialog,
} from './TaskAssigneeLabel.styles';

interface IRecentUser {
  id: string;
  date: Date;
}

interface ITaskAssigneeLabel {
  task: ITaskRawQuery;
}

export const TaskAssigneeLabel = ({ task }: ITaskAssigneeLabel) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [recentUsers, setRecentUsers] = useLocalStorage<IRecentUser[]>(
    'recentTaskAssignee',
    [],
  );

  const searchLocal = useState<string>('');
  const searchUser = useState<string>('');

  const { data, isLoading, isError } = useQuery('members', getUserMembers);

  const mutation = useMutation(patchTask, {
    onSuccess: async (_, { assigneeId: assignee }) => {
      queryClient.invalidateQueries(['tasks']);

      toast.success('Task changed successfully');

      if (assignee) {
        setRecentUsers(
          [
            { id: assignee, date: new Date() },
            ...recentUsers.filter(item => item.id !== assignee),
          ].slice(0, 5),
        );
      }
    },
    onError: () => {
      toast.error('Something got wrong');
    },
  });

  const findCurrentUser = data?.find(item => item.id === task.user?.id);

  return (
    <Container onClick={e => e.stopPropagation()}>
      <Dialog
        align="center"
        menuButton={
          <LabelContainer>
            {task.user ? (
              <>
                <LabelImage
                  brokenSrc={ProfileDefaultPNG}
                  src={task.user.avatar || ProfileDefaultPNG}
                />
                <Name>{nameToShortName(task.user.name)}</Name>
              </>
            ) : (
              <>
                <ImageEmpty src={ProfileDefaultPNG} />
                <EmptyName>Assign</EmptyName>
              </>
            )}
          </LabelContainer>
        }
      >
        <Header>
          <Title>{task.user ? 'Change Assignee' : 'Assign to'}</Title>
          <Close>
            <BlueIcon icon="cross" color="#8F95B2" />
          </Close>
        </Header>
        <Divider />

        <InputContainer>
          <SearchIconContainer>
            <BlueIcon icon="search" size={12} color="#8F95B2" />
          </SearchIconContainer>
          <InputSearch
            placeholder="User name or Email"
            value={searchLocal.get()}
            onChange={e => searchLocal.set(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                searchUser.set(searchLocal.get());
              }
            }}
          />
          {searchUser.get() && (
            <SearchCloseIconContainer
              onClick={() => {
                searchUser.set('');
                searchLocal.set('');
              }}
            >
              <BlueIcon icon="small-cross" color="#8F95B2" />
            </SearchCloseIconContainer>
          )}
        </InputContainer>

        <Items>
          {isLoading && <TaskAssigneeLabelLoading />}

          {!isLoading && (
            <Items>
              {findCurrentUser && (
                <>
                  <Label className="h1">Current Assignee</Label>
                  <ItemNoFeedback>
                    <Icon>
                      <Image
                        src={findCurrentUser.picture || AvatarDefaultImg}
                        brokenSrc={AvatarDefaultImg}
                      />
                    </Icon>
                    <Label>{findCurrentUser.name}</Label>
                    <Remove
                      onClick={() => {
                        if (!mutation.isLoading) {
                          mutation.mutate({
                            taskId: task.id,
                            assigneeId: null,
                          });
                        }
                      }}
                    >
                      <BlueIcon icon="cross" color="#D14343" size={12} />
                    </Remove>
                  </ItemNoFeedback>
                </>
              )}

              {!isLoading &&
                !isError &&
                data &&
                (() => {
                  const filteredUsers = data
                    .filter(item => item.id !== task.user?.id)
                    .filter(item =>
                      searchUser.get()
                        ? item.name
                            ?.toLowerCase()
                            .includes(searchUser.get().toLowerCase()) ||
                          item.email
                            ?.toLowerCase()
                            .includes(searchUser.get().toLowerCase())
                        : true,
                    );

                  const showRecent =
                    filteredUsers.filter(
                      item =>
                        !!recentUsers.find(userItem => userItem.id === item.id),
                    ).length > 0;

                  const showNoRecent =
                    filteredUsers.filter(
                      item =>
                        !recentUsers.find(userItem => userItem.id === item.id),
                    ).length > 0;

                  const showSeparator = showRecent && showNoRecent;

                  return (
                    <>
                      {findCurrentUser && (showRecent || showNoRecent) && (
                        <UsersSeparator />
                      )}
                      {showRecent && <Label className="h1">Recent</Label>}
                      {recentUsers
                        .map(item =>
                          filteredUsers.find(
                            userItem => userItem.id === item.id,
                          ),
                        )
                        .filter(
                          item =>
                            !!recentUsers.find(
                              userItem => userItem.id === item?.id,
                            ),
                        )
                        .map(item => (
                          <Item
                            key={item?.id}
                            onClick={() => {
                              if (!mutation.isLoading) {
                                mutation.mutate({
                                  taskId: task.id,
                                  assigneeId: item?.id,
                                });
                              }
                            }}
                          >
                            <Icon>
                              <Image
                                src={item?.picture || AvatarDefaultImg}
                                brokenSrc={AvatarDefaultImg}
                              />
                            </Icon>
                            <Label>{item?.name}</Label>
                          </Item>
                        ))}

                      {showRecent && showSeparator && <UsersSeparator />}

                      {filteredUsers
                        .filter(
                          item =>
                            !recentUsers.find(
                              userItem => userItem.id === item.id,
                            ),
                        )
                        .map(item => (
                          <Item
                            key={item.id}
                            onClick={() => {
                              if (!mutation.isLoading) {
                                mutation.mutate({
                                  taskId: task.id,
                                  assigneeId: item.id,
                                });
                              }
                            }}
                          >
                            <Icon>
                              <Image
                                src={item.picture || AvatarDefaultImg}
                                brokenSrc={AvatarDefaultImg}
                              />
                            </Icon>
                            <Label>{item.name}</Label>
                          </Item>
                        ))}
                    </>
                  );
                })()}
            </Items>
          )}
        </Items>
      </Dialog>
    </Container>
  );
};
